import axios from 'axios';
import { LoginCredentials, User } from '../types/auth';
import { API_ENDPOINTS } from '../config/api';
import { createAuthError, logError, parseAxiosError } from '../utils/errorUtils';
import { ticketApi } from './ticketApi';

const LOGIN_URL = API_ENDPOINTS.login;
const LOCAL_API_URL = API_ENDPOINTS.users;
const FIND_BY_PHONE_URL = API_ENDPOINTS.usersFindByPhone;

class AuthService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('access_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('access_token', token);
  }

  getToken(): string | null {
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('userInfo');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  async login(credentials: LoginCredentials): Promise<User> {
    try {
      const response = await axios.post(`${LOGIN_URL}`, credentials);
      const { data } = response.data;

      if (!data || !data.token) {
        const authError = createAuthError('Giriş bilgileri doğrulanamadı. Token alınamadı.');
        logError(authError, 'Auth token missing');
        throw authError;
      }

      this.setToken(data.token);

      // User objesini oluştur (token hariç)
      const user: User = {
        name: data.name,
        surname: data.surname,
        phone_number: data.phone,
        identification_number: data.identification_number,
        management: data.management,
        office: data.office,
        authorization_level: data.authorization_level,
      };

      // Önce kullanıcıyı telefon numarası ile backend'de ara
      let dbUser;
      try {
        const findRes = await axios.post(FIND_BY_PHONE_URL, { phone_number: user.phone_number });
        dbUser = findRes.data;
      } catch (err) {
        // User not found in local DB is not an error, we'll create them
        dbUser = null;
      }

      if (!dbUser) {
        // Kullanıcı yoksa ekle
        try {
          const createResponse = await axios.post(LOCAL_API_URL, user);
          dbUser = createResponse.data;
        } catch (dbError) {
          // Log but don't throw - user can still login even if local DB fails
          logError(dbError as Error, 'Failed to save user to local database');
        }
      }

      // Kullanıcının fiş bakiyesini getir
      let userWithBalance = user;
      if (dbUser?.id) {
        try {
          const balance = await ticketApi.getUserBalance(dbUser.id);
          // Veritabanından gelen bilgileri de dahil et (office bilgisi dahil)
          userWithBalance = {
            ...user,
            id: dbUser.id,
            ticket_count: balance,
            management: dbUser.management || user.management,
            office: dbUser.office || user.office,
          };
        } catch (balanceError) {
          // Bakiye alınamazsa varsayılan değer kullan
          userWithBalance = {
            ...user,
            id: dbUser.id,
            ticket_count: 0,
            management: dbUser.management || user.management,
            office: dbUser.office || user.office,
          };
          logError(balanceError as Error, 'Failed to fetch user balance');
        }
      }

      localStorage.setItem('userInfo', JSON.stringify(userWithBalance));
      return userWithBalance;
    } catch (error: any) {
      // If it's already an AppError, just re-throw
      if (error.type) {
        throw error;
      }

      // Parse axios error or create generic auth error
      if (error?.isAxiosError || error?.response) {
        const appError = parseAxiosError(error);
        logError(appError, 'Login request failed');
        throw appError;
      }

      // Generic error
      const authError = createAuthError(
        error?.message || 'Giriş işlemi sırasında beklenmeyen bir hata oluştu'
      );
      logError(authError, 'Login operation failed');
      throw authError;
    }
  }

  async logout(): Promise<void> {
    this.clearToken();
  }

  // Kullanıcı bilgilerini veritabanından yenile
  async refreshUserInfo(userId: number): Promise<User> {
    // Önce mevcut localStorage verilerini al
    let existingUser: User | null = null;
    try {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        existingUser = JSON.parse(userInfoStr);
      }
    } catch (error) {
      logError(error as Error, 'Failed to parse existing user info from localStorage');
    }

    try {
      // Kullanıcı bilgilerini veritabanından getir
      const userResponse = await axios.get(`${LOCAL_API_URL}/${userId}`);
      const dbUser = userResponse.data;

      if (!dbUser) {
        throw createAuthError('Kullanıcı bilgileri bulunamadı');
      }

      // Güncel fiş bakiyesini getir
      let ticketCount = 0;
      try {
        ticketCount = await ticketApi.getUserBalance(userId);
      } catch (balanceError) {
        logError(balanceError as Error, 'Failed to fetch updated balance');
        // Bakiye alınamazsa, mevcut bakiyeyi koru
        ticketCount = existingUser?.ticket_count || 0;
      }

      const updatedUser: User = {
        id: dbUser.id,
        name: dbUser.name,
        surname: dbUser.surname,
        phone_number: dbUser.phone_number,
        identification_number: dbUser.identification_number,
        management: dbUser.management,
        office: dbUser.office,
        authorization_level: dbUser.authorization_level,
        ticket_count: ticketCount,
      };

      // LocalStorage'ı güncelle
      localStorage.setItem('userInfo', JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error: any) {
      // Hata durumunda, mevcut localStorage verilerini koru
      // Sadece timestamp'i güncelle ki veri fresh görünsün
      if (existingUser) {
        logError(error as Error, 'API error during refresh, keeping existing localStorage data');

        // Eğer ticket_count 0 ise, onu localStorage'te de 0 yap
        // Bu sayede veritabanında bakiye değişse bile localStorage güncel kalır
        try {
          // Sadece bakiye için API'yi dene
          const ticketCount = await ticketApi.getUserBalance(userId);
          existingUser.ticket_count = ticketCount;
          localStorage.setItem('userInfo', JSON.stringify(existingUser));
        } catch (balanceError) {
          // Bakiye bile alınamazsa, en azından mevcut veriyi koru
          logError(balanceError as Error, 'Could not fetch balance, keeping existing data');
          localStorage.setItem('userInfo', JSON.stringify(existingUser));
        }

        return existingUser;
      }

      // If it's already an AppError, just re-throw
      if (error.type) {
        throw error;
      }

      // Parse axios error or create generic auth error
      if (error?.isAxiosError || error?.response) {
        const appError = parseAxiosError(error);
        logError(appError, 'User info refresh failed');
        throw appError;
      }

      // Generic error
      const authError = createAuthError(
        error?.message || 'Bilgi yenileme sırasında beklenmeyen bir hata oluştu'
      );
      logError(authError, 'User info refresh operation failed');
      throw authError;
    }
  }
}

export const authService = new AuthService();
